/**
 * The HUD's attention rule (docs/UI-CONSISTENCY.md "Action first"). One small state machine says how busy the
 * player is — `calm` (on foot, no heat, nothing going on), `busy` (driving, a job on) or `action` (wanted, a
 * chase, a fight, driving fast) — and one table says what each kind of HUD piece may do in each state. Every
 * piece asks here instead of carrying its own rule: on a phone in a city-wide chase the screen had the open
 * objective clipping with its hint, two toasts, NOW ENTERING in the middle of the road and the home-screen
 * hint over the police bark, and about a third of the game left to look at.
 *
 * Pure (no DOM, no services): the UI system feeds it facts each frame and applies the answers.
 */
import type { ToastKind, ToastPriority } from './uiTypes';

export type { ToastPriority };

export type Attention = 'calm' | 'busy' | 'action';

/** What the UI system knows each frame. */
export interface AttentionFacts {
  /** Wanted level (0 = nobody after you). */
  heat: number;
  /** Seconds since the player was last hurt, fired, or hit someone (Infinity: never). */
  sinceCombat: number;
  /** Driving speed in mph, 0 on foot. */
  mph: number;
  onFoot: boolean;
  /** A menu, the book, a panel, a result sheet, the title: the HUD is not the thing being looked at. */
  menu: boolean;
}

/** Driving at least this fast is action: the road ahead needs the screen. */
export const ACTION_MPH = 45;
/** A fight keeps the HUD in action this long after the last blow or shot. */
export const COMBAT_S = 6;
/** Action ends this long after the last reason for it (no flapping at a junction, or as the heat drops). */
export const ACTION_HOLD_S = 4;

export class HudAttention {
  mode: Attention = 'calm';
  private hold = 0;

  update(dt: number, f: AttentionFacts): Attention {
    const hot = f.heat >= 1 || f.sinceCombat < COMBAT_S || (!f.onFoot && f.mph >= ACTION_MPH);
    if (hot) this.hold = ACTION_HOLD_S;
    else this.hold = Math.max(0, this.hold - dt);
    this.mode = this.hold > 0 ? 'action' : f.onFoot && f.heat === 0 && !f.menu ? 'calm' : 'busy';
    return this.mode;
  }
}

export const toastPriority = (kind: ToastKind, given?: ToastPriority): ToastPriority => given ?? (kind === 'bad' ? 'urgent' : 'normal');

/** In action at most this many toasts are up at once (two everywhere else on a phone). */
export const ACTION_TOASTS = 1;
/** In action an urgent toast says it short. */
export const ACTION_TOAST_S = 3.2;
/** In action the same urgent line is said once in this long. */
export const ACTION_REPEAT_S = 30;
/** Toasts held back in action wait this long for calm, then go (they were about then, not now). */
export const HELD_TOAST_S = 30;

/**
 * What to do with a toast: `show` it, `hold` it until the action is over, or `merge` it (a nudge: it is held
 * too, but only the newest nudge is kept — the pager's slips during a chase come out as one, the book has the
 * rest and the pager counts them).
 */
export function toastPolicy(p: ToastPriority, mode: Attention): 'show' | 'hold' | 'merge' {
  if (mode !== 'action' || p === 'urgent') return 'show';
  return p === 'nudge' ? 'merge' : 'hold';
}

/**
 * The rest of the HUD in one table: what each piece may do in each state.
 *  - `hint`: the "Play full screen" card (src/ui/mobile.ts) — only when calm; it waits for a calm moment.
 *  - `tip`: the onboarding tip slip — not in action (its clock stops; it comes back once things are calm).
 *  - `district`: NOW ENTERING — skipped in action (the minimap's street plate names the district anyway).
 *  - `objective`: the clipping on a phone — in action it stays folded to its strip (a tap still opens it)
 *    and does not open by itself on a new step.
 * Subtitles (the police bark, a client on the line) are never held, and nothing above may cover them.
 */
export const HUD_RULES: Record<'hint' | 'tip' | 'district' | 'objectiveAutoOpen', Record<Attention, boolean>> = {
  hint: { calm: true, busy: false, action: false },
  tip: { calm: true, busy: true, action: false },
  district: { calm: true, busy: true, action: false },
  objectiveAutoOpen: { calm: true, busy: true, action: false },
};

export const allowed = (piece: keyof typeof HUD_RULES, mode: Attention): boolean => HUD_RULES[piece][mode];
